/**
 * Parr theme JS (mf-theme.js) — owner: theme-core. ES module, loaded from layout/theme.liquid (defer).
 * Only touches `[data-mf-*]` hooks. Public API on `window.MF`. Contract: theme-work/CSS-API.md
 *
 * Cart events — Horizon 4.2.0 (verified in theme-work/full/assets):
 *   Horizon listens to `StandardEvents.cartLinesUpdate` = 'shopify:cart:lines-update' (package `@shopify/events`,
 *   importmap snippets/scripts.liquid:14). Listeners: cart-icon.js:31, cart-drawer.js:34 (auto-open on action 'add'),
 *   component-cart-items.js:141 (morphs `detail.sections[sectionId]`), header-actions.js:19.
 *   product-form.js:439-455 dispatches `new CartLinesUpdateEvent({ action:'add', context:'product', lines:[{merchandiseId, quantity}],
 *   promise })` and resolves it (product-form.js:546) with `{ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(/cart.js),
 *   detail: { items, source, sourceId, itemCount, productId, sections, didError } }`. We dispatch the exact same shape.
 */
let SE = null;
try { SE = await import('@shopify/events'); } catch (e) { /* CDN blocked → plain events only */ }

const CART_EVENT = (SE && SE.StandardEvents && SE.StandardEvents.cartLinesUpdate) || 'shopify:cart:lines-update';
const CART_ERROR_EVENT = (SE && SE.StandardEvents && SE.StandardEvents.cartError) || 'shopify:cart:error';
const routes = () => (window.Theme && window.Theme.routes) || {};
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const on = (type, sel, fn, opts) => document.addEventListener(type, (e) => { const t = e.target instanceof Element ? e.target.closest(sel) : null; if (t) fn(e, t); }, opts);
// Horizon 4.2.0 scrolls .page-wrapper (not the window) from 990px up — base.css:27-55. Read both.
const pageWrapper = () => document.querySelector('.page-wrapper');
const scrollTop = () => { const pw = pageWrapper(); return Math.max(window.scrollY || 0, pw ? pw.scrollTop : 0); };
const onScroll = (fn) => { window.addEventListener('scroll', fn, { passive: true }); const pw = pageWrapper(); if (pw) pw.addEventListener('scroll', fn, { passive: true }); };
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const MF = (window.MF = window.MF || {});
MF.version = '1.0.0';
MF.scrollTop = scrollTop; MF.onScroll = onScroll;
/** Breakpoints (DESIGN-SPEC §9). `nav` = header switch, read from the header section setting. */
MF.bp = { mobile: 767, tablet: 1024, get nav() { const h = $('[data-mf-header]'); return Number(h && h.dataset.mfNavBp) || 1024; } };
MF.mq = {
  mobile: () => window.matchMedia(`(max-width: ${MF.bp.mobile}px)`).matches,
  tablet: () => window.matchMedia(`(max-width: ${MF.bp.tablet}px)`).matches,
  navMobile: () => window.matchMedia(`(max-width: ${MF.bp.nav}px)`).matches,
};
MF.events = {
  cartUpdate: 'mf:cart:update',           // detail: { cart } raw /cart.js json, or { cart: null, normalized } when relayed from Horizon
  cartAdd: 'mf:cart:add',                 // detail: { items, cart }
  cartError: 'mf:cart:error',             // detail: { message, response }
  quickviewOpen: 'mf:quickview:open',     // detail: { handle, root }
  quickviewClose: 'mf:quickview:close',
  offcanvasToggle: 'mf:offcanvas:toggle', // detail: { id, open }
  searchToggle: 'mf:search:toggle',       // detail: { open }
  carouselChange: 'mf:carousel:change',   // detail: { index, root }
  horizonCartLinesUpdate: CART_EVENT,
  horizonCartError: CART_ERROR_EVENT,
};
const emit = (name, detail = {}, target = document) => target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

/* ---------------- money ---------------- */
const moneyFormat = () => { const el = $('[data-mf-money-format]'); return (el && el.getAttribute('data-mf-money-format')) || '${{amount}}'; };
function formatMoney(cents, format = moneyFormat()) {
  const n = Number(cents) / 100;
  const fixed = (d, thousands = ',', decimal = '.') => { const p = n.toFixed(d).split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands); return p.join(decimal); };
  const map = {
    amount: fixed(2), amount_no_decimals: fixed(0),
    amount_with_comma_separator: fixed(2, '.', ','), amount_no_decimals_with_comma_separator: fixed(0, '.', ','),
    amount_with_space_separator: fixed(2, ' ', ','), amount_no_decimals_with_space_separator: fixed(0, ' ', ','),
    amount_with_apostrophe_separator: fixed(2, "'", '.'),
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (map[k] !== undefined ? map[k] : map.amount)).replace(/<[^>]+>/g, '');
}
MF.formatMoney = formatMoney;

/* ---------------- cart state ---------------- */
function updateCartUI({ count, totalCents }) {
  if (count !== undefined) $$('[data-mf-cart-count]').forEach((el) => { el.textContent = count > 0 ? String(count) : ''; el.classList.toggle('is-empty', !count); });
  if (totalCents !== undefined) $$('[data-mf-cart-total]').forEach((el) => { el.textContent = formatMoney(totalCents); });
  if (count !== undefined) $$('[data-mf-cart-live]').forEach((el) => { el.textContent = `${el.dataset.mfCartLive || 'Cart'}: ${count}`; });
}
async function fetchCart() {
  const r = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return r.json();
}
async function refreshCart() {
  const cart = await fetchCart();
  updateCartUI({ count: cart.item_count, totalCents: cart.total_price });
  emit(MF.events.cartUpdate, { cart });
  return cart;
}
MF.refreshCart = refreshCart;

// Mirror Horizon's own add/remove/qty updates into our header count.
document.addEventListener(CART_EVENT, (event) => {
  if (!event.promise) { refreshCart().catch(() => {}); return; }
  event.promise.then(({ cart, detail }) => {
    if (detail && detail.source === 'mf-theme') return null; // our own dispatch — already handled
    if (!cart) return refreshCart();
    const amount = Number(cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount);
    updateCartUI({ count: cart.totalQuantity || 0, totalCents: Number.isFinite(amount) ? Math.round(amount * 100) : undefined });
    emit(MF.events.cartUpdate, { cart: null, normalized: cart });
    return null;
  }).catch(() => {});
});
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart().catch(() => {}); });

/* ---------------- Horizon cart drawer ---------------- */
const cartDrawer = () => $('#cart-drawer');
const drawerAutoOpens = () => !!$('#cart-drawer cart-drawer-component[auto-open]');
async function openCart() {
  const drawer = cartDrawer();
  if (!drawer) { location.href = routes().cart_url || '/cart'; return false; }
  try { await customElements.whenDefined('theme-drawer'); } catch (e) { /* noop */ }
  if (typeof drawer.open === 'function') { closeSearch(); closeOffcanvas(); drawer.open(); return true; }
  location.href = routes().cart_url || '/cart';
  return false;
}
MF.openCart = openCart;
on('click', '[data-mf-cart-open]', (e) => { if (cartDrawer()) { e.preventDefault(); openCart(); } });

/* ---------------- add to cart ---------------- */
const cartSectionIds = () => $$('cart-items-component[data-section-id]').map((el) => el.dataset.sectionId).filter(Boolean);
function showFormError(source, message) {
  const root = source instanceof HTMLFormElement ? source : null;
  const el = root ? (root.querySelector('[data-mf-add-error]') || (root.parentElement && root.parentElement.querySelector('[data-mf-add-error]'))) : null;
  if (el) { el.textContent = message; el.hidden = !message; } else if (message) console.warn('[mf-theme] add to cart:', message);
}
/**
 * @param {HTMLFormElement|{id:string|number, quantity?:number, properties?:object, selling_plan?:string}} source
 * @param {{openDrawer?:boolean, button?:HTMLElement}} opts
 */
async function addToCart(source, opts = {}) {
  const isForm = source instanceof HTMLFormElement;
  const fd = isForm ? new FormData(source) : new FormData();
  if (!isForm) {
    fd.set('id', String(source.id));
    fd.set('quantity', String(source.quantity || 1));
    if (source.selling_plan) fd.set('selling_plan', String(source.selling_plan));
    if (source.properties) for (const [k, v] of Object.entries(source.properties)) fd.set(`properties[${k}]`, String(v));
  }
  if (!fd.get('quantity')) fd.set('quantity', '1');
  const sections = cartSectionIds();
  if (sections.length) { fd.set('sections', sections.join(',')); fd.set('sections_url', location.pathname); }
  const quantity = Number(fd.get('quantity')) || 1;
  const merchandiseId = String(fd.get('id') || '');
  const button = opts.button;
  if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }

  const target = isForm ? source : (button || document);
  let deferred = null;
  if (SE && SE.CartLinesUpdateEvent) {
    deferred = SE.CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(new SE.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId, quantity }], promise: deferred.promise }));
  }
  const resolveHorizon = (cart, detail) => { if (deferred) deferred.resolve({ cart: cart && SE ? SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null, detail: { source: 'mf-theme', itemCount: quantity, ...detail } }); };
  try {
    const res = await fetch(routes().cart_add_url || '/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
    const json = await res.json();
    if (!res.ok || json.status) {
      const message = json.description || json.message || 'Could not add to cart';
      if (SE && SE.CartErrorEvent) target.dispatchEvent(new SE.CartErrorEvent({ error: json.message || message, code: 'INVALID', detail: { description: json.description, errors: json.errors } }));
      const cart = await refreshCart().catch(() => null);
      resolveHorizon(cart, { didError: true, items: cart ? cart.items : [] });
      emit(MF.events.cartError, { message, response: json }, target);
      showFormError(source, message);
      return { ok: false, message, response: json };
    }
    const cart = await refreshCart();
    const items = json.items || [json];
    resolveHorizon(cart, { didError: false, sections: json.sections, items: cart.items, productId: items[0] && items[0].product_id });
    emit(MF.events.cartAdd, { items, cart }, target);
    showFormError(source, '');
    // Horizon opens the drawer itself when settings.auto_open_cart_drawer is on (cart-drawer.js #handleCartLinesUpdate)
    if (opts.openDrawer !== false && !drawerAutoOpens()) { closeModal(); openCart(); } else closeModal();
    return { ok: true, items, cart };
  } catch (err) {
    if (deferred) deferred.reject(err);
    if (isForm) { source.removeAttribute('data-mf-add-to-cart'); source.submit(); } else { location.href = routes().cart_url || '/cart'; }
    return { ok: false, message: String(err) };
  } finally {
    if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
  }
}
MF.addToCart = addToCart;
document.addEventListener('submit', (e) => {
  const form = e.target instanceof Element ? e.target.closest('form[data-mf-add-to-cart]') : null;
  if (!form) return;
  e.preventDefault();
  addToCart(form, { button: form.querySelector('[type="submit"]') || undefined });
});
on('click', 'button[data-mf-add-to-cart], a[data-mf-add-to-cart]', (e, btn) => {
  if (btn.closest('form[data-mf-add-to-cart]')) return;
  const id = btn.getAttribute('data-mf-add-to-cart') || btn.dataset.variantId;
  if (!id) return;
  e.preventDefault();
  const scope = btn.closest('[data-mf-qty-scope]');
  const qtyInput = scope ? scope.querySelector('input[name="quantity"]') : null;
  addToCart({ id, quantity: Number(qtyInput && qtyInput.value) || Number(btn.dataset.quantity) || 1 }, { button: btn });
});

/* ---------------- quantity ± ---------------- */
on('click', '[data-mf-qty-plus], [data-mf-qty-minus]', (e, btn) => {
  e.preventDefault();
  const scope = btn.closest('[data-mf-qty]') || btn.closest('[data-mf-qty-scope]') || btn.parentElement;
  const input = scope && scope.querySelector('input[type="number"], input[name="quantity"], input[name="updates[]"]');
  if (!input) return;
  const step = Number(input.step) || 1;
  const min = input.min !== '' ? Number(input.min) : 1;
  const max = input.max !== '' ? Number(input.max) : Infinity;
  const v = Math.min(max, Math.max(min, (Number(input.value) || 0) + (btn.hasAttribute('data-mf-qty-plus') ? step : -step)));
  input.value = String(v);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------------- overlay + off-canvas (mobile menu, filters) ---------------- */
let overlay = null;
const openStack = [];
function ensureOverlay() {
  if (overlay && overlay.isConnected) return overlay;
  // Prefer the overlay rendered by mf-header (same stacking context as the drawer/search panel).
  const existing = $('[data-mf-overlay]');
  if (existing) {
    overlay = existing;
    if (!overlay.dataset.mfOverlayReady) { overlay.dataset.mfOverlayReady = '1'; overlay.addEventListener('click', () => { closeOffcanvas(); closeSearch(); closeModal(); }); }
    return overlay;
  }
  overlay = document.createElement('div');
  overlay.className = 'mf-overlay';
  overlay.setAttribute('data-mf-overlay', '');
  overlay.addEventListener('click', () => { closeOffcanvas(); closeSearch(); closeModal(); });
  document.body.appendChild(overlay);
  return overlay;
}
function syncOverlay() {
  const anyOpen = openStack.length > 0 || !!$('.mf-search.is-open') || !!$('.mf-modal.is-open');
  ensureOverlay().classList.toggle('is-open', openStack.length > 0 || !!$('.mf-search.is-open'));
  document.body.classList.toggle('mf-scroll-lock', anyOpen);
  // Horizon's own lock (base.css: html[scroll-lock] .page-wrapper { overflow: hidden }) — only release what we set
  const html = document.documentElement;
  if (anyOpen && !html.hasAttribute('scroll-lock')) { html.setAttribute('scroll-lock', ''); html.dataset.mfScrollLock = '1'; }
  else if (!anyOpen && html.dataset.mfScrollLock) { html.removeAttribute('scroll-lock'); delete html.dataset.mfScrollLock; }
}
let lastFocus = null;
function openOffcanvas(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  closeSearch();
  lastFocus = document.activeElement;
  el.classList.add('is-open');
  el.setAttribute('aria-hidden', 'false');
  el.removeAttribute('inert');
  if (!openStack.includes(el)) openStack.push(el);
  syncOverlay();
  const focusTarget = el.querySelector('[data-mf-offcanvas-close]') || el;
  if (typeof focusTarget.focus === 'function') focusTarget.focus({ preventScroll: true });
  $$(`[data-mf-offcanvas-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  emit(MF.events.offcanvasToggle, { id, open: true }, el);
  return true;
}
function closeOffcanvas(id) {
  const targets = id ? [document.getElementById(id)].filter(Boolean) : openStack.slice();
  targets.forEach((el) => {
    if (!el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
    const i = openStack.indexOf(el); if (i > -1) openStack.splice(i, 1);
    $$(`[data-mf-offcanvas-open="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    emit(MF.events.offcanvasToggle, { id: el.id, open: false }, el);
  });
  syncOverlay();
  if (targets.length && lastFocus && typeof lastFocus.focus === 'function') { lastFocus.focus({ preventScroll: true }); lastFocus = null; }
}
MF.openOffcanvas = openOffcanvas; MF.closeOffcanvas = closeOffcanvas;
on('click', '[data-mf-offcanvas-open]', (e, btn) => { e.preventDefault(); const id = btn.getAttribute('data-mf-offcanvas-open'); const el = document.getElementById(id); if (el && el.classList.contains('is-open')) closeOffcanvas(id); else openOffcanvas(id); });
on('click', '[data-mf-offcanvas-close]', (e, btn) => { e.preventDefault(); const oc = btn.closest('[data-mf-offcanvas]'); closeOffcanvas(btn.getAttribute('data-mf-offcanvas-close') || (oc && oc.id) || undefined); });

/* ---------------- search panel (header) ---------------- */
function openSearch() {
  const panel = $('[data-mf-search]');
  if (!panel) { location.href = routes().search_url || '/search'; return false; }
  closeOffcanvas();
  lastFocus = document.activeElement;
  panel.classList.add('is-open');
  panel.setAttribute('aria-hidden', 'false');
  panel.removeAttribute('inert');
  syncOverlay();
  $$('[data-mf-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  const input = panel.querySelector('input[type="search"]');
  if (input) setTimeout(() => input.focus({ preventScroll: true }), reducedMotion() ? 0 : 50);
  emit(MF.events.searchToggle, { open: true }, panel);
  return true;
}
function closeSearch() {
  const panel = $('[data-mf-search]');
  if (!panel || !panel.classList.contains('is-open')) return;
  panel.classList.remove('is-open');
  panel.setAttribute('aria-hidden', 'true');
  panel.setAttribute('inert', '');
  syncOverlay();
  $$('[data-mf-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  if (lastFocus && typeof lastFocus.focus === 'function') { lastFocus.focus({ preventScroll: true }); lastFocus = null; }
  emit(MF.events.searchToggle, { open: false }, panel);
}
MF.openSearch = openSearch; MF.closeSearch = closeSearch;
on('click', '[data-mf-search-open]', (e) => { e.preventDefault(); const p = $('[data-mf-search]'); if (p && p.classList.contains('is-open')) closeSearch(); else openSearch(); });
on('click', '[data-mf-search-close]', (e) => { e.preventDefault(); closeSearch(); });

// Predictive results: /search/suggest.json (products only), rendered as plain links.
let suggestTimer = 0; let suggestCtrl = null;
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
on('input', '[data-mf-search] input[type="search"]', (e, input) => {
  const panel = input.closest('[data-mf-search]');
  const out = panel && panel.querySelector('[data-mf-search-results]');
  if (!out) return;
  clearTimeout(suggestTimer);
  const q = input.value.trim();
  if (q.length < 2) { out.innerHTML = ''; return; }
  suggestTimer = setTimeout(async () => {
    try {
      if (suggestCtrl) suggestCtrl.abort();
      suggestCtrl = new AbortController();
      const base = routes().predictive_search_url || '/search/suggest';
      const res = await fetch(`${base}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`, { signal: suggestCtrl.signal, headers: { Accept: 'application/json' } });
      const json = await res.json();
      const products = (json.resources && json.resources.results && json.resources.results.products) || [];
      const allLabel = out.dataset.mfAllLabel || 'View all results';
      const emptyLabel = out.dataset.mfEmptyLabel || 'No products found';
      const searchUrl = `${routes().search_url || '/search'}?q=${encodeURIComponent(q)}&type=product`;
      out.innerHTML = products.length
        ? products.map((p) => `<a class="mf-search__result" href="${esc(p.url)}">${p.featured_image && p.featured_image.url ? `<img src="${esc(p.featured_image.url)}&width=96" alt="" width="48" height="72" loading="lazy">` : ''}<span><span class="mf-search__result-title">${esc(p.title)}</span><br><span class="mf-search__result-price">${esc(formatMoney(Math.round(Number(p.price) * 100)))}</span></span></a>`).join('') + `<a class="mf-search__all" href="${esc(searchUrl)}">${esc(allLabel)}</a>`
        : `<p class="mf-search__result">${esc(emptyLabel)}</p>`;
    } catch (err) { if (err && err.name !== 'AbortError') out.innerHTML = ''; }
  }, 250);
});

/* ---------------- modal / quick view (Section Rendering API) ---------------- */
function ensureModal() {
  let modal = $('#mf-quickview-modal');
  if (modal) return modal;
  modal = document.createElement('div');
  modal.id = 'mf-quickview-modal';
  modal.className = 'mf-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-hidden', 'true');
  modal.innerHTML = '<div class="mf-modal__backdrop" data-mf-modal-close></div><div class="mf-modal__dialog"><button type="button" class="mf-modal__close" data-mf-modal-close aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg></button><div class="mf-modal__body" data-mf-modal-body></div></div>';
  document.body.appendChild(modal);
  return modal;
}
function closeModal() {
  const modal = $('#mf-quickview-modal');
  if (!modal || !modal.classList.contains('is-open')) return;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  syncOverlay();
  emit(MF.events.quickviewClose, {}, modal);
}
const qvCache = new Map();
async function openQuickView(handle, sectionId = 'mf-quickview') {
  const modal = ensureModal();
  const body = modal.querySelector('[data-mf-modal-body]');
  modal.classList.add('is-open', 'is-loading');
  modal.setAttribute('aria-hidden', 'false');
  syncOverlay();
  try {
    let html = qvCache.get(handle);
    if (!html) {
      const root = (routes().root_url || '/').replace(/\/$/, '');
      const res = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`quick view ${res.status}`);
      html = await res.text();
      qvCache.set(handle, html);
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('.shopify-section') || doc.body;
    body.innerHTML = inner.innerHTML;
    body.querySelectorAll('script').forEach((old) => { const s = document.createElement('script'); Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value)); s.textContent = old.textContent; old.replaceWith(s); });
    initCarousels(body); initHoverImages(body);
    emit(MF.events.quickviewOpen, { handle, root: body }, modal);
    const closeBtn = modal.querySelector('.mf-modal__close'); if (closeBtn) closeBtn.focus({ preventScroll: true });
  } catch (err) {
    console.warn('[mf-theme] quick view failed, redirecting', err);
    location.href = `/products/${handle}`;
  } finally {
    modal.classList.remove('is-loading');
  }
}
MF.openQuickView = openQuickView; MF.closeQuickView = closeModal;
on('click', '[data-mf-quickview]', (e, btn) => { const h = btn.getAttribute('data-mf-quickview'); if (!h) return; e.preventDefault(); openQuickView(h, btn.getAttribute('data-mf-quickview-section') || 'mf-quickview'); });
on('click', '[data-mf-modal-close]', (e) => { e.preventDefault(); closeModal(); });
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeModal(); closeSearch(); closeOffcanvas(); } });

/* ---------------- carousel (scroll-snap) ---------------- */
const boundCarousels = new WeakSet();
function initCarousels(root = document) {
  const cars = $$('[data-mf-carousel]', root);
  if (root instanceof Element && root.matches('[data-mf-carousel]')) cars.unshift(root);
  cars.forEach((car) => {
    if (boundCarousels.has(car)) return; // WeakSet, not an attribute: a morph can strip attributes but keeps listeners
    boundCarousels.add(car);
    const track = car.querySelector('[data-mf-carousel-track]');
    if (!track) return;
    const prev = car.querySelector('[data-mf-carousel-prev]');
    const next = car.querySelector('[data-mf-carousel-next]');
    const step = () => { const first = track.firstElementChild; const cs = getComputedStyle(track); const gap = parseFloat(cs.columnGap || cs.gap) || 0; return first ? first.getBoundingClientRect().width + gap : track.clientWidth; };
    const atStart = () => track.scrollLeft <= 2;
    const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    const loop = car.hasAttribute('data-mf-carousel-loop');
    const behavior = () => (reducedMotion() ? 'auto' : 'smooth');
    const update = () => {
      const overflow = track.scrollWidth > track.clientWidth + 2;
      car.classList.toggle('has-overflow', overflow);
      if (prev) prev.disabled = !overflow || (!loop && atStart());
      if (next) next.disabled = !overflow || (!loop && atEnd());
      const idx = Math.round(track.scrollLeft / Math.max(1, step()));
      if (car.dataset.mfIndex !== String(idx)) { car.dataset.mfIndex = String(idx); emit(MF.events.carouselChange, { index: idx, root: car }, car); }
      car.querySelectorAll('[data-mf-carousel-dot]').forEach((d, i) => d.classList.toggle('is-active', i === idx));
    };
    const go = (dir) => {
      if (loop && dir > 0 && atEnd()) { track.scrollTo({ left: 0, behavior: behavior() }); return; }
      if (loop && dir < 0 && atStart()) { track.scrollTo({ left: track.scrollWidth, behavior: behavior() }); return; }
      track.scrollBy({ left: dir * step() * (Number(car.dataset.mfCarouselStep) || 1), behavior: behavior() });
    };
    if (prev) prev.addEventListener('click', (e) => { e.preventDefault(); go(-1); });
    if (next) next.addEventListener('click', (e) => { e.preventDefault(); go(1); });
    car.querySelectorAll('[data-mf-carousel-dot]').forEach((d, i) => d.addEventListener('click', () => track.scrollTo({ left: i * step(), behavior: behavior() })));
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
    new ResizeObserver(update).observe(track);
    const auto = Number(car.dataset.mfCarouselAutoplay);
    if (auto > 0 && !reducedMotion()) {
      let timer = setInterval(() => go(1), auto);
      const stop = () => clearInterval(timer);
      const start = () => { stop(); timer = setInterval(() => go(1), auto); };
      car.addEventListener('pointerenter', stop); car.addEventListener('pointerleave', start);
      car.addEventListener('focusin', stop); car.addEventListener('focusout', start);
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    }
    car.mfCarousel = { go, update, next: () => go(1), prev: () => go(-1), goTo: (i) => track.scrollTo({ left: i * step(), behavior: behavior() }) };
    update();
  });
}
MF.initCarousels = initCarousels;

/* ---------------- hover image (lazy 2nd image) ---------------- */
const boundHover = new WeakSet();
/** Idempotent: each wrapper is bound once (WeakSet); load() reads data-src live, so a morph that swaps the
 *  2nd image (or strips its src) is picked up on the next hover. Also accepts the wrapper itself as root. */
function initHoverImages(root = document) {
  const wraps = $$('[data-mf-hover-img]', root);
  if (root instanceof Element && root.matches('[data-mf-hover-img]')) wraps.unshift(root);
  wraps.forEach((wrap) => {
    if (boundHover.has(wrap)) return;
    boundHover.add(wrap);
    const load = () => {
      const alt = wrap.querySelector('.mf-hover-img__alt, img[data-src]');
      const src = alt && alt.getAttribute('data-src');
      if (!src || alt.getAttribute('src') === src) return;
      alt.setAttribute('src', src);
      const ss = alt.getAttribute('data-srcset'); if (ss) alt.setAttribute('srcset', ss);
    };
    wrap.addEventListener('pointerenter', load, { passive: true });
    wrap.addEventListener('focusin', load);
  });
}
MF.initHoverImages = initHoverImages;

/* ---------------- header: heights + sticky (always | scroll-up | none) ---------------- */
function initHeader() {
  const section = $('.mf-header-section');
  const header = $('[data-mf-header]');
  if (!section || !header) return;
  const mode = header.dataset.mfSticky || 'scroll-up';
  section.classList.remove('is-scrolled', 'is-hidden');
  const setVars = () => {
    const h = section.offsetHeight;
    document.documentElement.style.setProperty('--mf-header-offset', `${h}px`);
    // Horizon only measures these when #header-component exists (its header section is disabled) — keep drawers/anchors right.
    document.body.style.setProperty('--header-height', `${h}px`);
    const group = $('#header-group');
    if (group) { let total = 0; for (const el of group.children) if (el instanceof HTMLElement) total += el.offsetHeight; document.body.style.setProperty('--header-group-height', `${total}px`); }
  };
  setVars();
  new ResizeObserver(setVars).observe(section);
  if (mode === 'none') return;
  let lastY = scrollTop(); let ticking = false;
  const apply = () => {
    ticking = false;
    const y = Math.max(0, scrollTop());
    const threshold = section.offsetHeight;
    const scrolled = y > threshold;
    section.classList.toggle('is-scrolled', scrolled);
    if (mode === 'scroll-up') {
      const panelOpen = !!$('.mf-search.is-open') || openStack.length > 0;
      if (!scrolled) section.classList.remove('is-hidden');
      else if (!panelOpen && Math.abs(y - lastY) > 4) section.classList.toggle('is-hidden', y > lastY);
    }
    lastY = y;
  };
  onScroll(() => { if (!ticking) { ticking = true; requestAnimationFrame(apply); } });
  header.addEventListener('focusin', () => section.classList.remove('is-hidden'));
  apply();
}

/* ---------------- auto-init for HTML swapped in later ----------------
 * Horizon 4.2.0 emits no generic "section rendered/morphed" event (grep theme-work/full/assets: morph.js,
 * section-renderer.js, section-hydration.js dispatch nothing; events.js only has media/quantity/megaMenu/zoom/
 * cart-section:restored/slideshow; facets.js:135/192 fire 'shopify:search:update' / 'shopify:collection:update'
 * BEFORE the new HTML arrives). Facets/sort/pagination/recommendations/quick view all end in DOM writes, so a
 * debounced MutationObserver re-runs the (idempotent) initialisers on added subtrees. */
function observeDom() {
  if (MF.observer) return;
  const SEL = '[data-mf-hover-img], [data-mf-carousel]';
  let pending = new Set(); let timer = 0;
  const flush = () => {
    timer = 0;
    const roots = Array.from(pending).filter((n) => n.isConnected); pending = new Set();
    roots.forEach((n) => { initHoverImages(n); initCarousels(n); });
    if (roots.length) emit('mf:dom:update', { roots });
  };
  MF.observer = new MutationObserver((records) => {
    for (const r of records) for (const n of r.addedNodes) {
      if (n.nodeType !== 1) continue;
      if (n.matches(SEL) || n.querySelector(SEL)) pending.add(n);
    }
    if (pending.size && !timer) timer = setTimeout(flush, 50);
  });
  MF.observer.observe(document.body, { childList: true, subtree: true });
}

/* ---------------- boot ---------------- */
function boot() {
  $$('[data-mf-offcanvas], [data-mf-search]').forEach((el) => { if (!el.classList.contains('is-open')) { el.setAttribute('aria-hidden', 'true'); el.setAttribute('inert', ''); } });
  initHeader();
  initCarousels();
  initHoverImages();
  // leaving the mobile layout closes the mobile drawer
  window.matchMedia(`(max-width: ${MF.bp.nav}px)`).addEventListener('change', (e) => { if (!e.matches) closeOffcanvas('mf-mobile-menu'); });
  document.addEventListener('shopify:section:load', (e) => { initCarousels(e.target); initHoverImages(e.target); if (e.target.querySelector && e.target.querySelector('[data-mf-header]')) initHeader(); });
  observeDom();
  MF.ready = true;
  emit('mf:ready', { MF });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
